// Dr Chrono API Integration Service

import { config } from "../config";
import { TSession } from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { logUIError } from "./api-misc";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;

export type TExchangeDrChronoTokenResponse = {
  message: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

export type DrChronoClinicType = "cameo-surgery" | "top-aesthetics";

export type TDrChronoAuthUrlResponse = {
  auth_url: string;
};

// Add this function to get the Dr. Chrono authorization URL
export const getDrChronoAuthUrl = async (
  clinicType: DrChronoClinicType,
): Promise<TDrChronoAuthUrlResponse> => {
  try {
    const response = await fetch(
      `${protocol}://${host}/integrations/v1/drchrono/${clinicType}/auth-url`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get Dr. Chrono auth URL: ${error}`);
    }

    const data: TDrChronoAuthUrlResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting Dr. Chrono auth URL:", error);
    throw error;
  }
};

// Top Aesthetics Integration Dr Chrono
export const exchangeDrChronoTokenTopAesthetics = async (
  code: string,
): Promise<TExchangeDrChronoTokenResponse> => {
  try {
    const response = await fetch(
      `${protocol}://${host}/integrations/v1/drchrono/top-aesthetics/exchange-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Dr. Chrono exchange failed: ${error}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error exchanging Dr. Chrono token:", error);
    logUIError({
      message: "DrChrono token exchange failed (top-aesthetics)",
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      context: {
        component: "api-integrations-drchrono",
        clinicType: "top-aesthetics",
        error: error instanceof Error ? error.message : String(error),
      },
    }).catch((logErr) =>
      console.warn("logUIError failed in api-integrations-drchrono", logErr),
    );
    throw error;
  }
};

export const initiateDrChronoOAuthTopAesthetics = async (): Promise<void> => {
  try {
    const authData = await getDrChronoAuthUrl("top-aesthetics");
    // Redirect to Dr. Chrono's OAuth page
    window.location.href = authData.auth_url;
  } catch (error) {
    console.error("Error initiating Dr. Chrono OAuth:", error);
    throw error;
  }
};

///////////////////////////////
// Cameo Surgery Integration
///////////////////////////////
export const exchangeDrChronoTokenCameoSurgery = async (
  code: string,
): Promise<TExchangeDrChronoTokenResponse> => {
  try {
    const response = await fetch(
      `${protocol}://${host}/integrations/v1/drchrono/cameo-surgery/exchange-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Dr. Chrono exchange failed: ${error}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error exchanging Dr. Chrono token:", error);
    throw error;
  }
};

export const initiateDrChronoOAuthCameoSurgery = async (): Promise<void> => {
  try {
    const authData = await getDrChronoAuthUrl("cameo-surgery");
    // Redirect to Dr. Chrono's OAuth page
    window.location.href = authData.auth_url;
  } catch (error) {
    console.error("Error initiating Dr. Chrono OAuth:", error);
    throw error;
  }
};

export type TRequestIntegrationAccessPayload = {
  integration_type: string;
  source?: string;
  page_url?: string;
  note?: string;
};

export type TRequestIntegrationAccessResponse = {
  status: string;
  message: string;
};

export const requestIntegrationAccess = async (
  session: TSession,
  payload: TRequestIntegrationAccessPayload,
): Promise<TRequestIntegrationAccessResponse> => {
  const requestPayload = {
    integration_type: payload.integration_type,
    source: payload.source ?? "integrations_page",
    page_url:
      payload.page_url ??
      (typeof window !== "undefined" ? window.location.href : undefined),
    note: payload.note ?? undefined,
  };

  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/integration/${session.clinicId}/integrations/request-access`,
    {
      method: "POST",
      body: JSON.stringify(requestPayload),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to request integration access: ${error}`);
  }

  return await response.json();
};

export type TDrChronoSyncAllRequest = {
  access_token: string;
};

export type TDrChronoSyncAllResponse = {
  status: string;
  message: string;
  patients_status: string;
  appointments_status: string;
};

// Function to trigger complete Dr. Chrono sync (patients + appointments)
export const syncAllDrChronoData = async (
  session: TSession,
  accessToken: string,
): Promise<TDrChronoSyncAllResponse> => {
  try {
    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/sync-all`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to sync Dr. Chrono data: ${error}`);
    }

    const data: TDrChronoSyncAllResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error syncing Dr. Chrono data:", error);
    logUIError({
      message: "DrChrono sync-all failed",
      page_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      user_email: session?.userEmail || undefined,
      clinic_name: session?.clinicName || undefined,
      clinic_id: session?.clinicId || undefined,
      context: {
        component: "api-integrations-drchrono",
        action: "sync-all",
        error: error instanceof Error ? error.message : String(error),
      },
    }).catch((logErr) =>
      console.warn("logUIError failed in api-integrations-drchrono", logErr),
    );
    throw error;
  }
};

export type TDrChronoTemplate = {
  id: number;
  name: string;
  doctor: string;
  archived: boolean;
  is_onpatient: boolean;
  is_persistent: boolean;
  updated_at: string;
};

export type TDrChronoTemplateField = {
  id: number;
  name: string;
  data_type: string;
  required: boolean;
  allowed_values?: string[];
  comment?: string;
  archived: boolean;
};

export type TDrChronoTemplateAnalysis = {
  template_info: {
    id: number;
    name: string;
    doctor: string;
    archived: boolean;
    is_onpatient: boolean;
    is_persistent: boolean;
    updated_at: string;
  };
  clinical_note_fields: TDrChronoTemplateField[];
  field_summary: {
    total_fields: number;
    field_types: Record<string, number>;
    required_fields: number;
    optional_fields: number;
  };
  raw_template_data: any;
};

export type TDrChronoTemplatesListResponse = {
  status: string;
  clinic_id: string;
  total_templates: number;
  templates: TDrChronoTemplate[];
};

export type TDrChronoTemplateDetailsResponse = {
  status: string;
  clinic_id: string;
  template_id: number;
  template: TDrChronoTemplate & {
    clinical_note_fields?: TDrChronoTemplateField[];
  };
};

export type TDrChronoTemplateInspectionResponse = {
  status: string;
  clinic_id: string;
  template_id: number;
  analysis?: TDrChronoTemplateAnalysis;
  template_name?: string;
  summary?: {
    total_fields: number;
    required_fields: number;
    field_types: Record<string, number>;
  };
  text_fields?: Array<{
    id: number;
    name: string;
    type: string;
    required: boolean;
  }>;
};

export type TDrChronoTextFieldsResponse = {
  status: string;
  clinic_id: string;
  template_id: number | null;
  total_text_fields: number;
  text_fields: TDrChronoTemplateField[];
  recommendation: {
    best_field: TDrChronoTemplateField | null;
    usage: string;
  };
};

// Get all available Dr. Chrono templates
export const getDrChronoTemplates = async (
  session: TSession,
  doctorId?: number,
): Promise<TDrChronoTemplatesListResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates`,
    );

    if (doctorId) {
      url.searchParams.append("doctor_id", doctorId.toString());
    }

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get Dr. Chrono templates: ${error}`);
    }

    const data: TDrChronoTemplatesListResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting Dr. Chrono templates:", error);
    throw error;
  }
};

// Get detailed information about a specific template
export const getDrChronoTemplateDetails = async (
  session: TSession,
  templateId: number,
  doctorId?: number,
): Promise<TDrChronoTemplateDetailsResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/${templateId}`,
    );

    if (doctorId) {
      url.searchParams.append("doctor_id", doctorId.toString());
    }

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get Dr. Chrono template details: ${error}`);
    }

    const data: TDrChronoTemplateDetailsResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting Dr. Chrono template details:", error);
    throw error;
  }
};

// Comprehensive inspection of a template structure
export const inspectDrChronoTemplate = async (
  session: TSession,
  templateId: number,
  format: "json" | "summary" = "json",
): Promise<TDrChronoTemplateInspectionResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/${templateId}/inspect`,
    );

    url.searchParams.append("format", format);

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to inspect Dr. Chrono template: ${error}`);
    }

    const data: TDrChronoTemplateInspectionResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error inspecting Dr. Chrono template:", error);
    throw error;
  }
};

export type TDrChronoTemplateFieldsResponse = {
  template_id: number;
  template_name: string;
  field_count: number;
  editable_fields: Array<{
    field_id: number;
    field_name: string;
    data_type: string;
    required: boolean;
    allowed_values?: string[];
    comment?: string;
    archived: boolean;
    value_instructions: string;
    how_to_set_value: string;
  }>;
};

// Get editable fields for a specific Dr. Chrono template
export const getDrChronoTemplateFields = async (
  session: TSession,
  templateId: number,
  doctorId?: number,
): Promise<TDrChronoTemplateFieldsResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/${templateId}/fields`,
    );

    if (doctorId) {
      url.searchParams.append("doctor_id", doctorId.toString());
    }

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get Dr. Chrono template fields: ${error}`);
    }

    const data: TDrChronoTemplateFieldsResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting Dr. Chrono template fields:", error);
    throw error;
  }
};

// Response type for the convert templates endpoint
// export type TConvertDrChronoTemplatesResponse = {
//   status: "success";
//   soap_config: {
//     clinic_id: string;
//     clinic_name: string;
//     subjective: {
//       section_name: "subjective";
//       fields: Record<
//         string,
//         {
//           type: "text";
//           description: string;
//           required: boolean;
//           drchrono_source: {
//             template_id: number;
//             field_id: number;
//             original_name: string;
//           };
//         }
//       >;
//     };
//     objective: {
//       section_name: "objective";
//       fields: Record<
//         string,
//         {
//           type: "text";
//           description: string;
//           required: boolean;
//           drchrono_source: {
//             template_id: number;
//             field_id: number;
//             original_name: string;
//           };
//         }
//       >;
//     };
//     assessment: {
//       section_name: "assessment";
//       fields: Record<
//         string,
//         {
//           type: "text";
//           description: string;
//           required: boolean;
//           drchrono_source: {
//             template_id: number;
//             field_id: number;
//             original_name: string;
//           };
//         }
//       >;
//     };
//     plan: {
//       section_name: "plan";
//       fields: Record<
//         string,
//         {
//           type: "text";
//           description: string;
//           required: boolean;
//           drchrono_source: {
//             template_id: number;
//             field_id: number;
//             original_name: string;
//           };
//         }
//       >;
//     };
//     output_mappings: {
//       drchrono: {
//         integration_type: "drchrono";
//         templates: Array<{
//           template_id: string;
//           template_name: string;
//           template_type: "mixed";
//           field_mappings: Record<string, string>;
//         }>;
//       };
//     };
//     processing_settings: {
//       extraction_prompts: {
//         subjective: string;
//         objective: string;
//         assessment: string;
//         plan: string;
//       };
//       quality_thresholds: {
//         minimum_completion_rate: number;
//         minimum_confidence_score: number;
//         require_hpi: boolean;
//         require_assessment: boolean;
//       };
//     };
//   };
// };

// // Request type for converting templates
// export type TConvertDrChronoTemplatesRequest = {
//   template_ids: number[];
// };

// // Convert Dr. Chrono templates to ClinicoS SOAP configuration
// export const convertDrChronoTemplatesToSoap = async (
//   session: TSession,
//   templateIds: number[],
// ): Promise<TConvertDrChronoTemplatesResponse> => {
//   try {
//     const url = new URL(
//       `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/convert-to-clinicos`,
//     );

//     const requestBody: TConvertDrChronoTemplatesRequest = {
//       template_ids: templateIds,
//     };

//     const response = await fetch(url.toString(), {
//       method: "POST",
//       headers: {
//         Authorization: `Bearer ${session.accessToken}`,
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(requestBody),
//       signal: AbortSignal.timeout(10 * 60 * 1000), // 10 minutes

//     });

//     if (!response.ok) {
//       const error = await response.text();
//       throw new Error(`Failed to convert Dr. Chrono templates: ${error}`);
//     }

//     const data: TConvertDrChronoTemplatesResponse = await response.json();
//     return data;
//   } catch (error) {
//     console.error("Error converting Dr. Chrono templates:", error);
//     throw error;
//   }
// };

// ============================================
// TYPE DEFINITIONS
// ============================================

// Background task status enum
export enum BackgroundTaskStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
}

// Request type for converting templates
export type TConvertDrChronoTemplatesRequest = {
  template_ids: number[];
};

// Response type for starting template conversion (background task)
export type TAutoConvertDrChronoTemplateResponse = {
  status: string;
  message: string;
  clinic_id: string;
  template_ids: number[];
  task_id: number;
};

// Response type for checking background task status
export type TBackgroundTaskStatusResponse = {
  id: number;
  clinic_uuid: string;
  task_name: string;
  status: BackgroundTaskStatus;
  result?: {
    status?: string;
    message?: string;
    clinic_id?: string;
    template_ids?: number[];
    soap_config?: any; // The full SOAP config from your original type
    error?: string;
  } | null;
  created_at: string; // ISO datetime string
  updated_at: string; // ISO datetime string
};

// Your existing complete SOAP config type (keeping for when task is completed)
export type TConvertDrChronoTemplatesResponse = {
  status: "success";
  soap_config: {
    clinic_id: string;
    clinic_name: string;
    subjective: {
      section_name: "subjective";
      fields: Record<
        string,
        {
          type: "text";
          description: string;
          required: boolean;
          drchrono_source: {
            template_id: number;
            field_id: number;
            original_name: string;
          };
        }
      >;
    };
    objective: {
      section_name: "objective";
      fields: Record<
        string,
        {
          type: "text";
          description: string;
          required: boolean;
          drchrono_source: {
            template_id: number;
            field_id: number;
            original_name: string;
          };
        }
      >;
    };
    assessment: {
      section_name: "assessment";
      fields: Record<
        string,
        {
          type: "text";
          description: string;
          required: boolean;
          drchrono_source: {
            template_id: number;
            field_id: number;
            original_name: string;
          };
        }
      >;
    };
    plan: {
      section_name: "plan";
      fields: Record<
        string,
        {
          type: "text";
          description: string;
          required: boolean;
          drchrono_source: {
            template_id: number;
            field_id: number;
            original_name: string;
          };
        }
      >;
    };
    output_mappings: {
      drchrono: {
        integration_type: "drchrono";
        templates: Array<{
          template_id: string;
          template_name: string;
          template_type: "mixed";
          field_mappings: Record<string, string>;
        }>;
      };
    };
    processing_settings: {
      extraction_prompts: {
        subjective: string;
        objective: string;
        assessment: string;
        plan: string;
      };
      quality_thresholds: {
        minimum_completion_rate: number;
        minimum_confidence_score: number;
        require_hpi: boolean;
        require_assessment: boolean;
      };
    };
  };
};

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Start background conversion of Dr. Chrono templates to ClinicoS SOAP configuration
 * @param session - User session containing clinic ID and access token
 * @param templateIds - Array of Dr. Chrono template IDs to convert
 * @returns Promise with task information for polling status
 */
export const startDrChronoTemplateConversion = async (
  session: TSession,
  templateIds: number[],
): Promise<TAutoConvertDrChronoTemplateResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/convert-to-clinicos`,
    );

    const requestBody: TConvertDrChronoTemplatesRequest = {
      template_ids: templateIds,
    };

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal: AbortSignal.timeout(30 * 1000), // 30 seconds for starting task
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(
        `Failed to start Dr. Chrono template conversion: ${error}`,
      );
    }

    const data: TAutoConvertDrChronoTemplateResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error starting Dr. Chrono template conversion:", error);
    throw error;
  }
};

/**
 * Poll the status of a template conversion background task
 * @param session - User session containing clinic ID and access token
 * @param taskId - ID of the background task to check
 * @returns Promise with current task status and result
 */
export const getDrChronoTemplateConversionStatus = async (
  session: TSession,
  taskId: number,
): Promise<TBackgroundTaskStatusResponse> => {
  try {
    const url = new URL(
      `${protocol}://${host}/clinics/v1/connector/drchrono/${session.clinicId}/templates/conversion-status/${taskId}`,
    );

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: AbortSignal.timeout(10 * 1000), // 10 seconds for status check
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get conversion status: ${error}`);
    }

    const data: TBackgroundTaskStatusResponse = await response.json();
    return data;
  } catch (error) {
    console.error(
      "Error getting Dr. Chrono template conversion status:",
      error,
    );
    throw error;
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Utility function to poll conversion status until completion
 * @param session - User session
 * @param taskId - Background task ID
 * @param options - Polling configuration
 * @returns Promise that resolves when task is complete with final result
 */
export const pollDrChronoTemplateConversion = async (
  session: TSession,
  taskId: number,
  options: {
    pollIntervalMs?: number;
    maxAttempts?: number;
    onProgress?: (status: TBackgroundTaskStatusResponse) => void;
  } = {},
): Promise<TBackgroundTaskStatusResponse> => {
  const {
    pollIntervalMs = 2000, // Poll every 2 seconds
    maxAttempts = 150, // Max 5 minutes of polling
    onProgress,
  } = options;

  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const status = await getDrChronoTemplateConversionStatus(session, taskId);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }

      // Check if task is complete
      if (status.status === BackgroundTaskStatus.COMPLETED) {
        return status;
      }

      if (status.status === BackgroundTaskStatus.FAILED) {
        throw new Error(
          `Template conversion failed: ${status.result?.error || "Unknown error"}`,
        );
      }

      // Task is still pending or in progress, wait and try again
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      attempts++;
    } catch (error) {
      console.error(
        `Error polling conversion status (attempt ${attempts + 1}):`,
        error,
      );
      throw error;
    }
  }

  throw new Error("Template conversion polling timed out");
};

/**
 * Complete workflow: Start conversion and poll until completion
 * @param session - User session
 * @param templateIds - Template IDs to convert
 * @param options - Polling options
 * @returns Promise with final conversion result
 */
export const convertDrChronoTemplatesWithPolling = async (
  session: TSession,
  templateIds: number[],
  options?: {
    pollIntervalMs?: number;
    maxAttempts?: number;
    onProgress?: (status: TBackgroundTaskStatusResponse) => void;
  },
): Promise<TBackgroundTaskStatusResponse> => {
  // Start the conversion
  const startResponse = await startDrChronoTemplateConversion(
    session,
    templateIds,
  );

  // Poll until completion
  const finalResult = await pollDrChronoTemplateConversion(
    session,
    startResponse.task_id,
    options,
  );

  return finalResult;
};
